<template>
  <list-header :title="$t('order.orders')">
    <template #viewToggle>
      <view-toggle
        :views="getOrdersViews()"
        :current-view="OrdersView.GRID"
        :toggle-event="OrderEvent.ToggleView"
        @changed="changeView"
      />
    </template>
    <template #action>
      <orders-grid-error-button :error-column="columns.ERROR" @click="showSideMenu = true" />
    </template>
  </list-header>
  <error-component v-if="mustHandleError" :error="error" transparent-background />
  <template v-else>
    <v-row density="comfortable" class="mr-1">
      <v-col
        v-for="shownPortalOrderStatus in shownPortalOrderStatuses"
        :key="shownPortalOrderStatus"
      >
        <orders-grid-column-header
          :order-status="columns[shownPortalOrderStatus].orderStatus"
          :count="columns[shownPortalOrderStatus].count"
          :loading="columns[shownPortalOrderStatus].loading"
        />
      </v-col>
    </v-row>
    <list-content ref="listContent" class="overflow-y-scroll">
      <v-col
        v-for="shownPortalOrderStatus in shownPortalOrderStatuses"
        :key="shownPortalOrderStatus"
      >
        <orders-grid-column :column="columns[shownPortalOrderStatus]" />
      </v-col>
      <v-col v-if="loadingNextPage" cols="12" class="d-flex justify-center">
        <v-progress-circular indeterminate :size="50" color="primary" class="ma-1" />
      </v-col>
    </list-content>
  </template>
  <orders-grid-error-side-menu
    :show="showSideMenu"
    :column="columns[PortalOrderStatus.ERROR]"
    @close="showSideMenu = false"
  />
</template>

<script setup lang="ts">
import ListContent from '@/components/list/ListContent.vue'
import ListHeader from '@/components/list/ListHeader.vue'
import ViewToggle from '@/components/ViewToggle.vue'
import ErrorComponent from '@/modules/error/components/ErrorComponent.vue'
import { customErrorHandling } from '@/modules/error/helpers'
import OrdersGridColumn from '@/modules/order/components/OrdersGridColumn.vue'
import OrdersGridColumnHeader from '@/modules/order/components/OrdersGridColumnHeader.vue'
import OrdersGridErrorButton from '@/modules/order/components/OrdersGridErrorButton.vue'
import OrdersGridErrorSideMenu from '@/modules/order/components/OrdersGridErrorSideMenu.vue'
import { getOrdersViews } from '@/modules/order/helpers'
import { OrderPreviewColumnsKey, OrdersView } from '@/modules/order/types'
import useOrderPreviews from '@/modules/order/useOrderPreviews'
import OrderEvent from '@/modules/tracking/events/order'
import router from '@/router'
import { PortalOrderStatus } from '@/types/index'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue'

defineOptions({ name: 'OrdersGrid' })

const { error, mustHandleError } = customErrorHandling()
const columnPortalOrderStatuses: OrderPreviewColumnsKey[] = [
  PortalOrderStatus.ARRIVED,
  PortalOrderStatus.PROCESSING,
  PortalOrderStatus.ADVISED,
  PortalOrderStatus.INVOICED,
  PortalOrderStatus.ERROR
]
const { columns, loadingNextPage } = storeToRefs(useOrderPreviews())
const page = ref(1)
const listContentRef = useTemplateRef('listContent')
const showSideMenu = ref(false)

onBeforeMount(() => {
  for (const columnPortalOrderStatus of columnPortalOrderStatuses) {
    useOrderPreviews().getOrderPreviews(columnPortalOrderStatus, page.value)
  }
})

const shownPortalOrderStatuses = computed<OrderPreviewColumnsKey[]>(() => {
  return [
    PortalOrderStatus.ARRIVED,
    PortalOrderStatus.PROCESSING,
    PortalOrderStatus.ADVISED,
    PortalOrderStatus.INVOICED
  ]
})

const getNextPage = () => {
  page.value = page.value + 1
  for (const columnPortalOrderStatus of columnPortalOrderStatuses) {
    useOrderPreviews().getAdditionalPage(columnPortalOrderStatus, page.value)
  }
}

const changeView = (view: OrdersView) => {
  router.push({ query: { view: view } })
}

onMounted(() => {
  if (listContentRef.value) {
    const listContentElement = listContentRef.value.$el
    listContentElement.addEventListener('scroll', () => {
      if (
        listContentElement.scrollTop + listContentElement.clientHeight >=
        listContentElement.scrollHeight
      ) {
        getNextPage()
      }
    })
  }
})
</script>
